// Reusable signature pad - canvas drawing with mouse/touch + name + agree checkbox
import React, { useRef, useState, useEffect } from 'react';
import { Eraser, Check } from 'lucide-react';

const SignaturePad = ({ onSign, signerName: defaultName = '', disabled = false }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [signerName, setSignerName] = useState(defaultName);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Resize to its CSS pixel size * DPR for crisp lines
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0a0a0a';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = async () => {
    if (!hasInk) return;
    if (!signerName.trim()) return;
    if (!agreed) return;
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await onSign({ signature: dataUrl, signer_name: signerName.trim(), agreed: true });
    } finally {
      setSubmitting(false);
    }
  };

  const canSign = hasInk && signerName.trim() && agreed && !disabled;

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <div className="bg-white rounded-lg border-2 border-gray-300 overflow-hidden relative" style={{ height: 160 }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-full block touch-none"
          style={{ cursor: disabled ? 'not-allowed' : 'crosshair' }}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <p className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm pointer-events-none italic">
            Sign here with your mouse or finger
          </p>
        )}
        <button
          type="button" onClick={clear} disabled={!hasInk || disabled}
          className="absolute top-2 right-2 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded flex items-center gap-1 disabled:opacity-40"
          data-testid="signature-clear-btn"
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>

      <input
        type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)}
        placeholder="Type your full legal name *" disabled={disabled}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:border-red-500 focus:outline-none"
        data-testid="signature-name-input"
      />

      <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
        <input
          type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={disabled}
          className="mt-0.5 w-4 h-4 accent-red-500"
          data-testid="signature-agree-checkbox"
        />
        <span>
          By signing, I confirm that I am the named customer and I authorize BELLAJA Electrical &amp; Security Systems to proceed with the work and charges as listed on this invoice.
        </span>
      </label>

      <button
        type="button" onClick={submit} disabled={!canSign || submitting}
        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded font-medium flex items-center justify-center gap-2 transition-colors"
        data-testid="signature-submit-btn"
      >
        <Check className="w-4 h-4" />
        {submitting ? 'Signing...' : 'Sign & Approve Invoice'}
      </button>
    </div>
  );
};

export default SignaturePad;
